import React, { useState, useEffect, createContext, useContext } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, signInWithPopup, signOut, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, addDoc, onSnapshot, doc, updateDoc, deleteDoc, query, orderBy, serverTimestamp } from 'firebase/firestore';

// =================================================================================================
// --- LOCAL DEVELOPMENT CONFIGURATION ---
// IMPORTANT: This block defines variables that are globally provided by the Canvas environment.
// For running on your local machine, you MUST place your actual Firebase project configuration here.
//
// Replace the placeholder values with the details from YOUR Firebase project.
// You obtained these from the Firebase Console when you added a web app.
// =================================================================================================
const myFirebaseConfig = {
  apiKey: "AIzaSyDP5bAkm0okpZ1CXYIrkPezMXtDPxfUcUc",
  authDomain: "crm-dc-c18ba.firebaseapp.com",
  projectId: "crm-dc-c18ba",
  storageBucket: "crm-dc-c18ba.firebasestorage.app",
  appId: "1:598854647237:web:df648f99458e59d5badfac",
  messagingSenderId: "598854647237" // Ensure messagingSenderId is also included
};

// These variables are used by the FirebaseProvider and Firestore collection paths.
// For local dev, we define them here. In Canvas, they are globally available.
const __app_id = myFirebaseConfig.projectId; // Using projectId as the app_id for consistency with Firestore paths
const __firebase_config = JSON.stringify(myFirebaseConfig);
const __initial_auth_token = null; // For Google Sign-In, you don't need a custom token initially.
                                  // The user will sign in via the UI.
// =================================================================================================
// --- END LOCAL DEVELOPMENT CONFIGURATION ---
// =================================================================================================


// Define Firebase context to pass Firebase instances and user state down the component tree
const FirebaseContext = createContext(null);

// Firebase Provider component to initialize Firebase and manage authentication state
export const FirebaseProvider = ({ children }) => {
    const [db, setDb] = useState(null); // Firestore instance
    const [auth, setAuth] = useState(null); // Auth instance
    const [user, setUser] = useState(null); // Full Firebase User object (contains displayName, email, photoURL, etc.)
    const [userId, setUserId] = useState(null); // User's unique ID (UID)
    const [loadingFirebase, setLoadingFirebase] = useState(true); // Loading state for Firebase initialization
    const [error, setError] = useState(null); // Error state for Firebase operations
    const [appIdContext, setAppIdContext] = useState('default-app-id'); // app ID for context

    useEffect(() => {
        const initializeFirebase = async () => {
            try {
                // Retrieve app ID and Firebase config from the environment (or local definitions above)
                // These lines ensure compatibility with both local setup and Canvas environment
                const currentAppId = typeof __app_id !== 'undefined' ? __app_id : 'default-app-id';
                const firebaseConfig = typeof __firebase_config !== 'undefined' ? JSON.parse(__firebase_config) : {};

                // Basic validation for Firebase config
                if (Object.keys(firebaseConfig).length === 0 || !firebaseConfig.apiKey) {
                    throw new Error("Firebase config not found or incomplete. Please ensure it's correctly set.");
                }

                // Initialize Firebase app
                const app = initializeApp(firebaseConfig);
                const firestore = getFirestore(app);
                const authInstance = getAuth(app);

                // Set Firebase instances to state
                setDb(firestore);
                setAuth(authInstance);
                setAppIdContext(currentAppId); // Set the app ID to context state

                // Listen for authentication state changes (user login/logout)
                onAuthStateChanged(authInstance, (currentUser) => {
                    if (currentUser) {
                        setUser(currentUser); // Set the full user object
                        setUserId(currentUser.uid); // Set the user's UID
                    } else {
                        setUser(null);
                        setUserId(null);
                    }
                    setLoadingFirebase(false); // Firebase initialization complete
                });

            } catch (err) {
                console.error("Failed to initialize Firebase:", err);
                setError(err.message);
                setLoadingFirebase(false);
            }
        };

        initializeFirebase();
    }, []); // Empty dependency array ensures this runs only once on component mount

    // Function to handle Google Sign-In using a popup
    const signInWithGoogle = async () => {
        if (!auth) {
            setError("Firebase Auth not initialized.");
            return;
        }
        try {
            const provider = new GoogleAuthProvider(); // Google Auth Provider
            await signInWithPopup(auth, provider); // Sign in with popup
            setError(null); // Clear any previous errors on successful sign-in
        } catch (err) {
            console.error("Error during Google Sign-In:", err);
            setError(`Google Sign-In failed: ${err.message}`);
        }
    };

    // Function to handle user sign-out
    const signOutGoogle = async () => {
        if (!auth) return; // Ensure auth is initialized
        try {
            await signOut(auth); // Sign out the current user
            setError(null); // Clear any previous errors on successful sign-out
        } catch (err) {
            console.error("Error during Sign-Out:", err);
            setError(`Sign-Out failed: ${err.message}`);
        }
    };

    return (
        <FirebaseContext.Provider value={{ db, auth, user, userId, loadingFirebase, error, signInWithGoogle, signOutGoogle, appId: appIdContext }}>
            {children} {/* Render child components */}
        </FirebaseContext.Provider>
    );
};

// Custom hook to easily access Firebase context values in any component
const useFirebase = () => {
    const context = useContext(FirebaseContext);
    if (!context) {
        // This error indicates useFirebase was called outside of a FirebaseProvider
        throw new Error('useFirebase must be used within a FirebaseProvider');
    }
    return context;
};

// Custom Confirmation Modal Component (replaces native alert/confirm)
const ConfirmationModal = ({ isOpen, message, onConfirm, onCancel }) => {
    if (!isOpen) return null; // Don't render if not open

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-xl shadow-2xl max-w-sm w-full mx-auto transform transition-all duration-300 scale-100 opacity-100">
                <p className="text-lg font-semibold text-gray-800 mb-6 text-center">{message}</p>
                <div className="flex justify-end space-x-3">
                    <button
                        onClick={onCancel}
                        className="px-5 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-300"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        className="px-5 py-2 bg-gradient-to-br from-red-500 to-red-700 text-white rounded-lg shadow-md hover:from-red-600 hover:to-red-800 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                    >
                        Confirm
                    </button>
                </div>
            </div>
        </div>
    );
};


// Component for adding new leads
const AddLeadForm = ({ onAddSuccess }) => {
    const { db, userId, appId } = useFirebase(); // Get db, userId, and appId from Firebase context
    const [collegeName, setCollegeName] = useState('');
    const [contactPerson, setContactPerson] = useState('');
    const [role, setRole] = useState('');
    const [email, setEmail] = useState('');
    const [mobileNumber, setMobileNumber] = useState('');
    const [universityLogoUrl, setUniversityLogoUrl] = useState('');
    const [studentCount, setStudentCount] = useState('');
    const [digitalCampusTier, setDigitalCampusTier] = useState('Not Selected');
    const [domain, setDomain] = useState('');
    const [collegeCity, setCollegeCity] = useState('');
    const [mailServiceProvider, setMailServiceProvider] = useState('Not Selected'); // New field
    const [timesContacted, setTimesContacted] = useState(0); // New field, default to 0
    const [status, setStatus] = useState('New');
    const [emailMarketingStatus, setEmailMarketingStatus] = useState('Not Sent');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');

    const statusOptions = ['New', 'Contacted', 'Qualified', 'Converted', 'Lost'];
    const emailMarketingStatusOptions = ['Not Sent', 'Sent', 'Opened', 'Clicked', 'Replied', 'Bounced', 'Unsubscribed'];
    const digitalCampusTierOptions = ['Not Selected', 'Standard', 'Premium', 'Elite'];
    const mailServiceProviderOptions = ['Not Selected', 'Google', 'Outlook', 'Own Server', 'Other']; // New options

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!db || !userId) {
            setMessage('Firebase not initialized or user not authenticated. Please sign in.');
            return;
        }
        if (!collegeName || !contactPerson || !email) {
            setMessage('Please fill in all required fields (College Name, Contact Person, Email).');
            return;
        }

        setLoading(true);
        setMessage('');

        try {
            const leadsCollectionRef = collection(db, `artifacts/${appId}/public/data/leads`);

            await addDoc(leadsCollectionRef, {
                collegeName,
                contactPerson,
                role,
                email,
                mobileNumber,
                universityLogoUrl,
                studentCount: Number(studentCount) || 0,
                digitalCampusTier,
                domain,
                collegeCity,
                mailServiceProvider, // Include new field
                timesContacted: Number(timesContacted) || 0, // Include new field
                status,
                emailMarketingStatus,
                responseHistory: [],
                userId,
                timestamp: serverTimestamp(),
            });
            setMessage('Lead added successfully!');
            setCollegeName('');
            setContactPerson('');
            setRole('');
            setEmail('');
            setMobileNumber('');
            setUniversityLogoUrl('');
            setStudentCount('');
            setDigitalCampusTier('Not Selected');
            setDomain('');
            setCollegeCity('');
            setMailServiceProvider('Not Selected'); // Reset new field
            setTimesContacted(0); // Reset new field
            setStatus('New');
            setEmailMarketingStatus('Not Sent');
            if (onAddSuccess) onAddSuccess();
        } catch (error) {
            console.error("Error adding lead:", error);
            setMessage(`Error adding lead: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-white p-8 rounded-xl shadow-xl mb-8 border border-gray-200">
            <h2 className="text-3xl font-extrabold text-gray-800 mb-8 text-center">Add New Lead</h2>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <label htmlFor="collegeName" className="block text-sm font-semibold text-gray-700 mb-1">College Name <span className="text-red-500">*</span></label>
                    <input
                        type="text"
                        id="collegeName"
                        value={collegeName}
                        onChange={(e) => setCollegeName(e.target.value)}
                        className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-base transition-all duration-200"
                        required
                    />
                </div>
                <div>
                    <label htmlFor="contactPerson" className="block text-sm font-semibold text-gray-700 mb-1">Contact Person <span className="text-red-500">*</span></label>
                    <input
                        type="text"
                        id="contactPerson"
                        value={contactPerson}
                        onChange={(e) => setContactPerson(e.target.value)}
                        className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-base transition-all duration-200"
                        required
                    />
                </div>
                <div>
                    <label htmlFor="role" className="block text-sm font-semibold text-gray-700 mb-1">Role</label>
                    <input
                        type="text"
                        id="role"
                        value={role}
                        onChange={(e) => setRole(e.target.value)}
                        className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-base transition-all duration-200"
                    />
                </div>
                <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
                    <input
                        type="email"
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-base transition-all duration-200"
                        required
                    />
                </div>
                <div>
                    <label htmlFor="mobileNumber" className="block text-sm font-semibold text-gray-700 mb-1">Mobile Number</label>
                    <input
                        type="tel"
                        id="mobileNumber"
                        value={mobileNumber}
                        onChange={(e) => setMobileNumber(e.target.value)}
                        className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-base transition-all duration-200"
                    />
                </div>
                <div>
                    <label htmlFor="universityLogoUrl" className="block text-sm font-semibold text-gray-700 mb-1">University Logo URL</label>
                    <input
                        type="url"
                        id="universityLogoUrl"
                        value={universityLogoUrl}
                        onChange={(e) => setUniversityLogoUrl(e.target.value)}
                        className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-base transition-all duration-200"
                        placeholder="e.g., https://example.com/logo.png"
                    />
                </div>
                <div>
                    <label htmlFor="studentCount" className="block text-sm font-semibold text-gray-700 mb-1">Student Approx Count</label>
                    <input
                        type="number"
                        id="studentCount"
                        value={studentCount}
                        onChange={(e) => setStudentCount(e.target.value)}
                        className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-base transition-all duration-200"
                        min="0"
                    />
                </div>
                <div>
                    <label htmlFor="digitalCampusTier" className="block text-sm font-semibold text-gray-700 mb-1">Digital Campus Tier</label>
                    <select
                        id="digitalCampusTier"
                        value={digitalCampusTier}
                        onChange={(e) => setDigitalCampusTier(e.target.value)}
                        className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-base transition-all duration-200 bg-white"
                    >
                        {digitalCampusTierOptions.map(option => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label htmlFor="domain" className="block text-sm font-semibold text-gray-700 mb-1">Domain</label>
                    <input
                        type="text"
                        id="domain"
                        value={domain}
                        onChange={(e) => setDomain(e.target.value)}
                        className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-base transition-all duration-200"
                        placeholder="e.g., exampleuniversity.edu"
                    />
                </div>
                <div>
                    <label htmlFor="collegeCity" className="block text-sm font-semibold text-gray-700 mb-1">College City</label>
                    <input
                        type="text"
                        id="collegeCity"
                        value={collegeCity}
                        onChange={(e) => setCollegeCity(e.target.value)}
                        className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-base transition-all duration-200"
                        placeholder="e.g., Bangalore"
                    />
                </div>
                <div>
                    <label htmlFor="mailServiceProvider" className="block text-sm font-semibold text-gray-700 mb-1">Mail Service Provider</label>
                    <select
                        id="mailServiceProvider"
                        value={mailServiceProvider}
                        onChange={(e) => setMailServiceProvider(e.target.value)}
                        className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-base transition-all duration-200 bg-white"
                    >
                        {mailServiceProviderOptions.map(option => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label htmlFor="timesContacted" className="block text-sm font-semibold text-gray-700 mb-1">Times Contacted</label>
                    <input
                        type="number"
                        id="timesContacted"
                        value={timesContacted}
                        onChange={(e) => setTimesContacted(e.target.value)}
                        className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-base transition-all duration-200"
                        min="0"
                    />
                </div>
                <div>
                    <label htmlFor="status" className="block text-sm font-semibold text-gray-700 mb-1">Lead Status</label>
                    <select
                        id="status"
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-base transition-all duration-200 bg-white"
                    >
                        {statusOptions.map(option => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label htmlFor="emailMarketingStatus" className="block text-sm font-semibold text-gray-700 mb-1">Email Marketing Status</label>
                    <select
                        id="emailMarketingStatus"
                        value={emailMarketingStatus}
                        onChange={(e) => setEmailMarketingStatus(e.target.value)}
                        className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-base transition-all duration-200 bg-white"
                    >
                        {emailMarketingStatusOptions.map(option => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </select>
                </div>
                <div className="md:col-span-2 mt-4">
                    <button
                        type="submit"
                        className="w-full flex justify-center py-3 px-6 border border-transparent rounded-lg shadow-lg text-lg font-bold text-white bg-gradient-to-br from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-300 transform hover:scale-105"
                        disabled={loading}
                    >
                        {loading ? 'Adding Lead...' : 'Add New Lead'}
                    </button>
                </div>
                {message && <p className="mt-4 text-base text-center text-red-600 font-medium">{message}</p>}
            </form>
        </div>
    );
};

// Component for displaying and editing a single lead's profile
const LeadProfilePage = ({ lead, onClose, onUpdate }) => {
    const { db, appId } = useFirebase(); // Get db and appId from Firebase context
    const [editedLead, setEditedLead] = useState(lead); // State for editable lead data
    const [newResponseText, setNewResponseText] = useState(''); // State for new response input
    const [updateMessage, setUpdateMessage] = useState(''); // Message for update feedback
    const [loadingUpdate, setLoadingUpdate] = useState(false); // Loading state for updates

    // Options for dropdowns (same as AddLeadForm)
    const statusOptions = ['New', 'Contacted', 'Qualified', 'Converted', 'Lost'];
    const emailMarketingStatusOptions = ['Not Sent', 'Sent', 'Opened', 'Clicked', 'Replied', 'Bounced', 'Unsubscribed'];
    const digitalCampusTierOptions = ['Not Selected', 'Standard', 'Premium', 'Elite'];
    const mailServiceProviderOptions = ['Not Selected', 'Google', 'Outlook', 'Own Server', 'Other']; // New options

    // Placeholder image for university logo
    const placeholderLogo = "https://placehold.co/150x150/e0e7ff/4338ca?text=No+Logo"; // Light blue background, indigo text

    // Update local state if the 'lead' prop changes (e.g., if the lead is updated in the list)
    useEffect(() => {
        setEditedLead(lead);
    }, [lead]);

    // Handle changes in input fields
    const handleFieldChange = (e) => {
        const { name, value } = e.target;
        setEditedLead(prev => ({ ...prev, [name]: value }));
    };

    // Handle saving changes to the lead's main profile fields
    const handleUpdate = async (e) => {
        e.preventDefault(); // Prevent default form submission
        if (!db) return;
        setLoadingUpdate(true);
        setUpdateMessage('');

        try {
            // Get Firestore document reference for the current lead
            // Uses the appId from context
            const leadDocRef = doc(db, `artifacts/${appId}/public/data/leads`, editedLead.id);
            // Update the document with new field values
            await updateDoc(leadDocRef, {
                collegeName: editedLead.collegeName,
                contactPerson: editedLead.contactPerson,
                role: editedLead.role,
                email: editedLead.email,
                mobileNumber: editedLead.mobileNumber,
                universityLogoUrl: editedLead.universityLogoUrl,
                studentCount: Number(editedLead.studentCount) || 0,
                digitalCampusTier: editedLead.digitalCampusTier,
                domain: editedLead.domain,
                collegeCity: editedLead.collegeCity,
                mailServiceProvider: editedLead.mailServiceProvider, // Include new field
                timesContacted: Number(editedLead.timesContacted) || 0, // Include new field
                status: editedLead.status,
                emailMarketingStatus: editedLead.emailMarketingStatus,
            });
            setUpdateMessage('Lead updated successfully!'); // Success message
            if (onUpdate) onUpdate(); // Notify parent of update
        } catch (error) {
            console.error("Error updating lead:", error);
            setUpdateMessage(`Error updating lead: ${error.message}`); // Display error message
        } finally {
            setLoadingUpdate(false); // Reset loading state
        }
    };

    // Handle adding a new response/interaction note
    const handleAddResponse = async () => {
        if (!db || !newResponseText.trim()) {
            setUpdateMessage('Response text cannot be empty.');
            return;
        }
        setLoadingUpdate(true);
        setUpdateMessage('');

        try {
            // Get Firestore document reference
            // Uses the appId from context
            const leadDocRef = doc(db, `artifacts/${appId}/public/data/leads`, editedLead.id);
            // Create a new response object
            const newResponse = {
                date: new Date().toISOString(), // Current timestamp
                text: newResponseText.trim(), // Trim whitespace from text
                status: editedLead.status, // Log the lead's status at the time of response
            };
            // Ensure responseHistory is an array (handle cases where it might be missing or not an array)
            const updatedResponseHistory = Array.isArray(editedLead.responseHistory)
                ? [...editedLead.responseHistory, newResponse] // Add new response to existing array
                : [newResponse]; // Create new array if none exists

            await updateDoc(leadDocRef, {
                responseHistory: updatedResponseHistory,
            });
            // Update local state to reflect the new response
            setEditedLead(prev => ({
                ...prev,
                responseHistory: updatedResponseHistory
            }));
            setNewResponseText(''); // Clear response input field
            setUpdateMessage('Response added successfully!'); // Success message
            if (onUpdate) onUpdate(); // Notify parent of update
        } catch (error) {
            console.error("Error adding response:", error);
            setUpdateMessage(`Error adding response: ${error.message}`); // Display error message
        } finally {
            setLoadingUpdate(false); // Reset loading state
        }
    };

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-40 p-4 sm:p-6">
            <div className="bg-white p-8 rounded-xl shadow-2xl max-w-3xl w-full mx-auto overflow-y-auto max-h-[90vh] border border-gray-200">
                <div className="flex justify-between items-center mb-8 border-b pb-4">
                    <h2 className="text-3xl font-extrabold text-gray-900">Lead Profile: {editedLead.collegeName}</h2>
                    <button
                        onClick={onClose} // Close button
                        className="text-gray-500 hover:text-gray-700 text-4xl font-semibold transition-transform duration-200 transform hover:rotate-90 focus:outline-none"
                    >
                        &times;
                    </button>
                </div>

                {/* University Logo Display */}
                <div className="flex justify-center mb-8">
                    <img
                        src={editedLead.universityLogoUrl || placeholderLogo}
                        alt={`${editedLead.collegeName} Logo`}
                        className="w-32 h-32 object-contain rounded-full border-2 border-blue-200 shadow-md"
                        onError={(e) => { e.target.onerror = null; e.target.src = placeholderLogo; }} // Fallback
                    />
                </div>

                {/* Form for editing main lead details */}
                <form onSubmit={handleUpdate} className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <div>
                        <label htmlFor="collegeName" className="block text-sm font-semibold text-gray-700 mb-1">College Name</label>
                        <input
                            type="text"
                            id="collegeName"
                            name="collegeName"
                            value={editedLead.collegeName}
                            onChange={handleFieldChange}
                            className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-base transition-all duration-200"
                            required
                        />
                    </div>
                    <div>
                        <label htmlFor="contactPerson" className="block text-sm font-semibold text-gray-700 mb-1">Contact Person</label>
                        <input
                            type="text"
                            id="contactPerson"
                            name="contactPerson"
                            value={editedLead.contactPerson}
                            onChange={handleFieldChange}
                            className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-base transition-all duration-200"
                            required
                        />
                    </div>
                    <div>
                        <label htmlFor="role" className="block text-sm font-semibold text-gray-700 mb-1">Role</label>
                        <input
                            type="text"
                            id="role"
                            name="role"
                            value={editedLead.role || ''}
                            onChange={handleFieldChange}
                            className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-base transition-all duration-200"
                        />
                    </div>
                    <div>
                        <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-1">Email</label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={editedLead.email}
                            onChange={handleFieldChange}
                            className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-base transition-all duration-200"
                            required
                        />
                    </div>
                    <div>
                        <label htmlFor="mobileNumber" className="block text-sm font-semibold text-gray-700 mb-1">Mobile Number</label>
                        <input
                            type="tel"
                            id="mobileNumber"
                            name="mobileNumber"
                            value={editedLead.mobileNumber || ''}
                            onChange={handleFieldChange}
                            className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-base transition-all duration-200"
                        />
                    </div>
                    <div>
                        <label htmlFor="universityLogoUrl" className="block text-sm font-semibold text-gray-700 mb-1">University Logo URL</label>
                        <input
                            type="url"
                            id="universityLogoUrl"
                            name="universityLogoUrl"
                            value={editedLead.universityLogoUrl || ''}
                            onChange={handleFieldChange}
                            className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-base transition-all duration-200"
                            placeholder="e.g., https://example.com/logo.png"
                        />
                    </div>
                    <div>
                        <label htmlFor="studentCount" className="block text-sm font-semibold text-gray-700 mb-1">Student Approx Count</label>
                        <input
                            type="number"
                            id="studentCount"
                            name="studentCount"
                            value={editedLead.studentCount || ''}
                            onChange={handleFieldChange}
                            className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-base transition-all duration-200"
                            min="0"
                        />
                    </div>
                    <div>
                        <label htmlFor="digitalCampusTier" className="block text-sm font-semibold text-gray-700 mb-1">Digital Campus Tier</label>
                        <select
                            id="digitalCampusTier"
                            name="digitalCampusTier"
                            value={editedLead.digitalCampusTier || 'Not Selected'}
                            onChange={handleFieldChange}
                            className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-base transition-all duration-200 bg-white"
                        >
                            {digitalCampusTierOptions.map(option => (
                                <option key={option} value={option}>{option}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label htmlFor="domain" className="block text-sm font-semibold text-gray-700 mb-1">Domain</label>
                        <input
                            type="text"
                            id="domain"
                            name="domain"
                            value={editedLead.domain || ''}
                            onChange={handleFieldChange}
                            className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-base transition-all duration-200"
                            placeholder="e.g., exampleuniversity.edu"
                        />
                    </div>
                    <div>
                        <label htmlFor="collegeCity" className="block text-sm font-semibold text-gray-700 mb-1">College City</label>
                        <input
                            type="text"
                            id="collegeCity"
                            name="collegeCity"
                            value={editedLead.collegeCity || ''}
                            onChange={handleFieldChange}
                            className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-base transition-all duration-200"
                            placeholder="e.g., Bangalore"
                        />
                    </div>
                    <div>
                        <label htmlFor="mailServiceProvider" className="block text-sm font-semibold text-gray-700 mb-1">Mail Service Provider</label>
                        <select
                            id="mailServiceProvider"
                            name="mailServiceProvider"
                            value={editedLead.mailServiceProvider || 'Not Selected'}
                            onChange={handleFieldChange}
                            className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-base transition-all duration-200 bg-white"
                        >
                            {mailServiceProviderOptions.map(option => (
                                <option key={option} value={option}>{option}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label htmlFor="timesContacted" className="block text-sm font-semibold text-gray-700 mb-1">Times Contacted</label>
                        <input
                            type="number"
                            id="timesContacted"
                            name="timesContacted"
                            value={editedLead.timesContacted || 0}
                            onChange={handleFieldChange}
                            className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-base transition-all duration-200"
                            min="0"
                        />
                    </div>
                    <div>
                        <label htmlFor="status" className="block text-sm font-semibold text-gray-700 mb-1">Lead Status</label>
                        <select
                            id="status"
                            name="status"
                            value={editedLead.status}
                            onChange={handleFieldChange}
                            className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-base transition-all duration-200 bg-white"
                        >
                            {statusOptions.map(option => (
                                <option key={option} value={option}>{option}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label htmlFor="emailMarketingStatus" className="block text-sm font-semibold text-gray-700 mb-1">Email Marketing Status</label>
                        <select
                            id="emailMarketingStatus"
                            name="emailMarketingStatus"
                            value={editedLead.emailMarketingStatus}
                            onChange={handleFieldChange}
                            className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-base transition-all duration-200 bg-white"
                        >
                            {emailMarketingStatusOptions.map(option => (
                                <option key={option} value={option}>{option}</option>
                            ))}
                        </select>
                    </div>
                    <div className="md:col-span-2 mt-4">
                        <button
                            type="submit"
                            className="w-full flex justify-center py-3 px-6 border border-transparent rounded-lg shadow-lg text-lg font-bold text-white bg-gradient-to-br from-green-600 to-green-800 hover:from-green-700 hover:to-green-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-300 transform hover:scale-105"
                            disabled={loadingUpdate}
                        >
                            {loadingUpdate ? 'Saving Changes...' : 'Save Changes'}
                        </button>
                        {updateMessage && <p className="mt-4 text-base text-center text-red-600 font-medium">{updateMessage}</p>}
                    </div>
                </form>

                {/* Section for Response History */}
                <div className="mt-8">
                    <h3 className="text-xl font-bold text-gray-800 mb-4 border-b pb-2">Response History</h3>
                    <div className="space-y-4 max-h-60 overflow-y-auto p-4 border border-gray-200 rounded-lg bg-gray-50 shadow-inner">
                        {editedLead.responseHistory && editedLead.responseHistory.length > 0 ? (
                            editedLead.responseHistory
                                .sort((a, b) => new Date(b.date) - new Date(a.date)) // Sort responses by date (latest first)
                                .map((response, index) => (
                                    <div key={index} className="bg-white p-4 rounded-lg shadow-md border border-gray-100">
                                        <p className="text-xs text-gray-500 mb-2 font-medium">
                                            {new Date(response.date).toLocaleString()} - Status: <span className="font-semibold text-blue-700">{response.status}</span>
                                        </p>
                                        <p className="text-sm text-gray-800 leading-relaxed">{response.text}</p>
                                    </div>
                                ))
                        ) : (
                            <p className="text-gray-500 text-base text-center py-4">No response history yet. Add a note below!</p>
                        )}
                    </div>
                    <div className="mt-6 flex flex-col sm:flex-row gap-3">
                        <textarea
                            value={newResponseText}
                            onChange={(e) => setNewResponseText(e.target.value)}
                            placeholder="Add a new response or interaction note..."
                            rows="3"
                            className="flex-grow px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-base transition-all duration-200 resize-y"
                        ></textarea>
                        <button
                            onClick={handleAddResponse}
                            className="sm:w-auto px-6 py-3 border border-transparent rounded-lg shadow-lg text-lg font-bold text-white bg-gradient-to-br from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-300 transform hover:scale-105"
                            disabled={loadingUpdate || !newResponseText.trim()} // Disable if loading or text is empty
                        >
                            Add Response
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};


// Component for listing all leads
const LeadList = ({ onSelectLead }) => {
    const { db, userId, loadingFirebase, error, appId } = useFirebase(); // Get db, userId, and appId from Firebase context
    const [leads, setLeads] = useState([]); // State to store fetched leads
    const [loadingLeads, setLoadingLeads] = useState(true); // Loading state for fetching leads
    const [filterStatus, setFilterStatus] = useState('All'); // State for filtering leads by status
    const [filterTier, setFilterTier] = useState('All'); // State for filtering by tier
    const [sortBy, setSortBy] = useState('timestamp'); // State for sorting column
    const [sortOrder, setSortOrder] = useState('desc'); // State for sort order (asc/desc)
    const [isModalOpen, setIsModalOpen] = useState(false); // State for confirmation modal visibility
    const [leadToDelete, setLeadToDelete] = useState(null); // State to store the lead to be deleted

    // Placeholder image for university logo in list
    const placeholderListLogo = "https://placehold.co/32x32/e0e7ff/4338ca?text=Logo";

    useEffect(() => {
        // Only fetch leads if Firebase is initialized and there are no errors
        if (!db || loadingFirebase || error) {
            setLoadingLeads(false);
            return;
        }

        // Construct Firestore collection path
        const leadsCollectionRef = collection(db, `artifacts/${appId}/public/data/leads`);
        // Create a query with dynamic ordering based on sortBy and sortOrder
        const q = query(leadsCollectionRef, orderBy(sortBy, sortOrder));

        // Set up a real-time listener for lead data changes
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const fetchedLeads = snapshot.docs.map(doc => ({
                id: doc.id, // Document ID from Firestore
                ...doc.data() // All other fields
            }));
            setLeads(fetchedLeads); // Update leads state
            setLoadingLeads(false); // Stop loading
        }, (err) => {
            console.error("Error fetching leads:", err);
            setLoadingLeads(false);
        });

        // Cleanup function: unsubscribe from the real-time listener when component unmounts
        return () => unsubscribe();
    }, [db, loadingFirebase, error, appId, sortBy, sortOrder]); // Dependencies: re-run effect if these change

    // Handle updating a lead's status directly from the table dropdown
    const handleStatusChange = async (leadId, newStatus) => {
        if (!db) return;
        const leadsCollectionRef = collection(db, `artifacts/${appId}/public/data/leads`);
        const leadDocRef = doc(leadsCollectionRef, leadId);
        try {
            await updateDoc(leadDocRef, { status: newStatus });
        } catch (error) {
            console.error("Error updating lead status:", error);
        }
    };

    // Handle updating a lead's email marketing status directly from the table dropdown
    const handleEmailMarketingStatusChange = async (leadId, newStatus) => {
        if (!db) return;
        const leadsCollectionRef = collection(db, `artifacts/${appId}/public/data/leads`);
        const leadDocRef = doc(leadsCollectionRef, leadId);
        try {
            await updateDoc(leadDocRef, { emailMarketingStatus: newStatus });
        } catch (error) {
            console.error("Error updating email marketing status:", error);
        }
    };

    const confirmDeleteLead = (lead) => {
        setLeadToDelete(lead);
        setIsModalOpen(true);
    };

    const handleDeleteConfirmed = async () => {
        if (!db || !leadToDelete) return;
        const leadsCollectionRef = collection(db, `artifacts/${appId}/public/data/leads`);
        const leadDocRef = doc(leadsCollectionRef, leadToDelete.id);

        try {
            await deleteDoc(leadDocRef);
            setIsModalOpen(false);
            setLeadToDelete(null);
        } catch (error) {
            console.error("Error deleting lead:", error);
        }
    };

    const handleDeleteCancelled = () => {
        setIsModalOpen(false);
        setLeadToDelete(null);
    };

    // Toggle sort order for 'timesContacted'
    const toggleSort = () => {
        setSortBy('timesContacted');
        setSortOrder(prevOrder => (prevOrder === 'asc' ? 'desc' : 'asc'));
    };


    if (loadingFirebase) {
        return <div className="text-center text-gray-600 text-lg py-8">Initializing Firebase and authentication...</div>;
    }

    if (error) {
        return <div className="text-center text-red-600 text-lg py-8">Error: {error}</div>;
    }

    if (!userId) {
        return <div className="text-center text-red-600 text-lg py-8">User not authenticated. Please sign in to view leads.</div>;
    }

    // Filter leads based on selected status and tier
    const filteredLeads = leads.filter(lead =>
        (filterStatus === 'All' || lead.status === filterStatus) &&
        (filterTier === 'All' || lead.digitalCampusTier === filterTier)
    );

    // Options for status and email marketing status dropdowns
    const statusOptions = ['All', 'New', 'Contacted', 'Qualified', 'Converted', 'Lost'];
    const emailMarketingStatusOptions = ['Not Sent', 'Sent', 'Opened', 'Clicked', 'Replied', 'Bounced', 'Unsubscribed'];
    const digitalCampusTierOptions = ['All', 'Not Selected', 'Standard', 'Premium', 'Elite'];


    return (
        <div className="bg-white p-8 rounded-xl shadow-xl border border-gray-200">
            <h2 className="text-3xl font-extrabold text-gray-800 mb-8 text-center">Current Leads</h2>

            <div className="mb-6 flex flex-col sm:flex-row items-center justify-between bg-gray-50 p-4 rounded-lg shadow-inner border border-gray-100">
                <div className="flex flex-col sm:flex-row gap-4">
                    <div className="mb-2 sm:mb-0">
                        <label htmlFor="filterStatus" className="block text-sm font-semibold text-gray-700 mb-1">Filter by Lead Status:</label>
                        <select
                            id="filterStatus"
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            className="mt-1 block w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-base transition-all duration-200 bg-white"
                        >
                            {statusOptions.map(status => (
                                <option key={status} value={status}>{status}</option>
                            ))}
                        </select>
                    </div>
                    <div className="mb-2 sm:mb-0">
                        <label htmlFor="filterTier" className="block text-sm font-semibold text-gray-700 mb-1">Filter by Tier:</label>
                        <select
                            id="filterTier"
                            value={filterTier}
                            onChange={(e) => setFilterTier(e.target.value)}
                            className="mt-1 block w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-base transition-all duration-200 bg-white"
                        >
                            {digitalCampusTierOptions.map(tier => (
                                <option key={tier} value={tier}>{tier}</option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className="text-base text-gray-600 font-medium mt-4 sm:mt-0">
                    Your User ID: <span className="font-bold text-gray-800 break-all">{userId}</span>
                </div>
            </div>

            {loadingLeads ? (
                <div className="text-center text-gray-600 text-lg py-8">Loading leads...</div>
            ) : filteredLeads.length === 0 ? (
                <p className="text-center text-gray-500 text-lg py-8">No leads found matching your criteria.</p>
            ) : (
                <div className="overflow-x-auto rounded-lg shadow-md border border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-100">
                            <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider rounded-tl-lg">Logo</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">College Name</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Contact Person</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Email</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Mobile</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Domain</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">City</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Students</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Tier</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Mail Provider</th> {/* New Column */}
                                <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                                    <button onClick={toggleSort} className="flex items-center text-gray-600 font-bold uppercase tracking-wider focus:outline-none">
                                        Times Contacted
                                        {sortBy === 'timesContacted' && (
                                            <span className="ml-1 text-blue-500">
                                                {sortOrder === 'asc' ? '▲' : '▼'}
                                            </span>
                                        )}
                                    </button>
                                </th> {/* New Column with sort */}
                                <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Lead Status</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Email Marketing Status</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider rounded-tr-lg">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredLeads.map((lead, index) => (
                                <tr key={lead.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        <img
                                            src={lead.universityLogoUrl || placeholderListLogo}
                                            alt={`${lead.collegeName} Logo`}
                                            className="w-8 h-8 object-contain rounded-full border border-gray-200"
                                            onError={(e) => { e.target.onerror = null; e.target.src = placeholderListLogo; }}
                                        />
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{lead.collegeName}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{lead.contactPerson}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{lead.email}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{lead.mobileNumber || 'N/A'}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{lead.domain || 'N/A'}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{lead.collegeCity || 'N/A'}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{lead.studentCount > 0 ? lead.studentCount.toLocaleString() : 'N/A'}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{lead.digitalCampusTier || 'N/A'}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{lead.mailServiceProvider || 'N/A'}</td> {/* Display Mail Service Provider */}
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{lead.timesContacted || 0}</td> {/* Display Times Contacted */}
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                        <select
                                            value={lead.status}
                                            onChange={(e) => handleStatusChange(lead.id, e.target.value)}
                                            className="block w-full px-3 py-1.5 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white transition-all duration-200"
                                        >
                                            {statusOptions.slice(1).map(status => (
                                                <option key={status} value={status}>{status}</option>
                                            ))}
                                        </select>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                        <select
                                            value={lead.emailMarketingStatus}
                                            onChange={(e) => handleEmailMarketingStatusChange(lead.id, e.target.value)}
                                            className="block w-full px-3 py-1.5 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white transition-all duration-200"
                                        >
                                            {emailMarketingStatusOptions.map(option => (
                                                <option key={option} value={option}>{option}</option>
                                            ))}
                                        </select>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <button
                                            onClick={() => onSelectLead(lead)}
                                            className="text-blue-600 hover:text-blue-800 transition-colors duration-200 rounded-md px-3 py-1.5 font-semibold"
                                        >
                                            View
                                        </button>
                                        <button
                                            onClick={() => confirmDeleteLead(lead)}
                                            className="text-red-600 hover:text-red-800 ml-4 transition-colors duration-200 rounded-md px-3 py-1.5 font-semibold"
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {/* Confirmation Modal for deletion */}
            <ConfirmationModal
                isOpen={isModalOpen}
                message={`Are you sure you want to delete lead for ${leadToDelete?.collegeName}? This action cannot be undone.`}
                onConfirm={handleDeleteConfirmed}
                onCancel={handleDeleteCancelled}
            />
        </div>
    );
};

// Main App Component that orchestrates the CRM application
const App = () => {
    // Get authentication and Firebase initialization states from context
    const { user, userId, loadingFirebase, error, signInWithGoogle, signOutGoogle } = useFirebase();
    const [selectedLead, setSelectedLead] = useState(null); // State to manage which lead's profile is currently open

    // Callback to set the selected lead for profile viewing
    const handleSelectLead = (lead) => {
        setSelectedLead(lead);
    };

    // Callback to close the lead profile page
    const handleCloseProfile = () => {
        setSelectedLead(null);
    };

    // Callback for when a lead is updated in the profile page.
    // In this setup, onSnapshot in LeadList automatically refreshes,
    // so explicit re-fetching here isn't strictly necessary but good for structure.
    const handleLeadUpdate = () => {
        // You could add specific logic here if needed, e.g., show a toast notification
    };

    return (
        // The FirebaseProvider now wraps the entire content that needs Firebase context.
        // This is done in index.js or main.jsx, not here directly in App.
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 sm:p-6 font-sans antialiased">
            {/* Tailwind CSS CDN for styling */}
            <script src="https://cdn.tailwindcss.com"></script>
            {/* Inter font for consistent typography */}
            <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
            <style>
                {`
                body {
                    font-family: 'Inter', sans-serif;
                }
                /* Custom scrollbar for better aesthetics */
                ::-webkit-scrollbar {
                    width: 8px;
                    height: 8px;
                }
                ::-webkit-scrollbar-track {
                    background: #f1f1f1;
                    border-radius: 10px;
                }
                ::-webkit-scrollbar-thumb {
                    background: #888;
                    border-radius: 10px;
                }
                ::-webkit-scrollbar-thumb:hover {
                    background: #555;
                }
                `}
            </style>
            <div className="max-w-5xl mx-auto py-8">
                <h1 className="text-5xl font-extrabold text-center text-gray-900 mb-12 drop-shadow-lg">
                    PRAGYASHAL <span className="text-blue-700">Lead CRM</span>
                </h1>

                {/* Authentication Section */}
                <div className="flex flex-col sm:flex-row justify-between items-center bg-white p-6 rounded-xl shadow-lg mb-10 border border-gray-200">
                    {loadingFirebase ? (
                        <p className="text-gray-600 text-lg font-medium">Loading authentication...</p>
                    ) : user ? ( // If user is authenticated
                        <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 sm:space-x-4 w-full justify-center sm:justify-start">
                            {user.photoURL && (
                                <img src={user.photoURL} alt="User Avatar" className="w-10 h-10 rounded-full border-2 border-blue-400 shadow-sm" />
                            )}
                            <p className="text-lg font-semibold text-gray-700">Welcome, <span className="text-blue-600">{user.displayName || user.email}</span>!</p>
                            <button
                                onClick={signOutGoogle}
                                className="px-6 py-2 bg-gradient-to-br from-red-500 to-red-700 text-white rounded-lg shadow-md hover:from-red-600 hover:to-red-800 transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                            >
                                Sign Out
                            </button>
                        </div>
                    ) : ( // If user is not authenticated
                        <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 sm:space-x-4 w-full justify-center sm:justify-start">
                            <p className="text-lg font-semibold text-gray-700">Please sign in to manage leads.</p>
                            <button
                                onClick={signInWithGoogle}
                                className="px-6 py-2 bg-gradient-to-br from-blue-600 to-blue-800 text-white rounded-lg shadow-md hover:from-blue-700 hover:to-blue-900 transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                            >
                                Sign In with Google
                            </button>
                        </div>
                    )}
                    {error && <p className="text-red-600 text-base mt-3 sm:mt-0 text-center sm:text-left">{error}</p>}
                </div>

                {/* Conditional rendering of CRM content based on authentication status */}
                {user ? ( // Only render CRM content if user is authenticated
                    selectedLead ? ( // If a lead is selected, show its profile page
                        <LeadProfilePage
                            lead={selectedLead}
                            onClose={handleCloseProfile}
                            onUpdate={handleLeadUpdate}
                        />
                    ) : ( // Otherwise, show the Add Lead form and Lead List
                        <>
                            <AddLeadForm />
                            <LeadList onSelectLead={handleSelectLead} />
                        </>
                    )
                ) : (
                    // Message to display when not authenticated
                    <p className="text-center text-gray-600 text-xl mt-12 p-6 bg-white rounded-xl shadow-lg border border-gray-200">Sign in with your Google account to access the CRM functionalities and start managing your leads!</p>
                )}
            </div>
        </div>
    );
};

export default App;
